import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, IconButton, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Stack,
  Dialog, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Slider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useRisiken, useCreateRisiko, useUpdateRisiko, useDeleteRisiko } from '../../../api/hooks/useRisiken';
import { useSyncMaturityFromCount } from '../../../api/hooks/useReifegrad';
import { useProject } from '../../../api/hooks/useProjects';
import { useNotifications } from '../../../context/NotificationContext';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { useAutosave } from '../../../hooks/useAutosave';
import { AutosaveIndicator } from '../../../components/common/AutosaveIndicator';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { buildRisikenReport } from '../reports/risikenReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';
import { PmCard, PmColumnPicker, PmHeadCell, useTableSortFilter, handleInlineEditKeyDown, type SortFilterColumn } from '../../../components/pm';
import { RisikoMatrix } from '../components/RisikoMatrix';
import { buildMovePatch } from '../components/riskScatterMath';
import { apiErrorMessage } from '../../../lib/apiErrorMessage';
import { AppDialogTitle } from '../../../components/common/AppDialogTitle';

interface Risiko {
  id: string;
  beschreibung: string;
  risiko: string | null;
  umgebung: string | null;
  weight: number;
  assessment: number;
  score: number;
  strategie: string | null;
  action: string | null;
  // Backend behaelt status; UI verwendet es nicht mehr.
  status?: string;
}

const STRATEGIES = ['Vermeiden', 'Vermindern', 'Übertragen', 'Akzeptieren'] as const;
const LEVELS = [1, 2, 3, 4, 5];
const DEFAULT_STATUS = 'Identifiziert';

interface CoreFields {
  beschreibung: string;
  risiko: string;
  umgebung: string;
  weight: number;
  assessment: number;
  strategie: string;
  action: string;
}

function coreFrom(r: Risiko): CoreFields {
  return {
    beschreibung: r.beschreibung,
    risiko: r.risiko ?? '',
    umgebung: r.umgebung ?? '',
    weight: r.weight,
    assessment: r.assessment,
    strategie: r.strategie ?? '',
    action: r.action ?? '',
  };
}

export function RisikenTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();

  const { data, isLoading } = useRisiken(projectId);
  const createMut = useCreateRisiko(projectId);
  const updateMut = useUpdateRisiko(projectId);
  const deleteMut = useDeleteRisiko(projectId);
  const items = ((data as unknown as { data?: Risiko[] } | undefined)?.data ?? []);
  useSyncMaturityFromCount(projectId, 12, isLoading ? -1 : items.length);

  const { data: projectResp } = useProject(projectId);
  const projectName =
    (projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name ?? 'Projekt';

  const [editItem, setEditItem] = useState<Risiko | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const columns = useMemo<SortFilterColumn<Risiko>[]>(() => [
    {
      key: 'beschreibung', label: t('risiken.beschreibung', 'Beschreibung'), hideable: false,
      filter: { type: 'text' }, value: (r) => r.beschreibung,
    },
    {
      key: 'risiko', label: t('risiken.risiko', 'Risiko'), defaultHidden: true,
      filter: { type: 'text' }, value: (r) => r.risiko ?? '',
    },
    {
      key: 'umgebung', label: t('risiken.umgebung', 'Umgebung'), defaultHidden: true,
      filter: { type: 'text' }, value: (r) => r.umgebung ?? '',
    },
    { key: 'weight', label: t('risiken.weight', 'Schwere'), align: 'center', value: (r) => r.weight },
    { key: 'assessment', label: t('risiken.assessment', 'Eintritt'), align: 'center', value: (r) => r.assessment },
    {
      key: 'strategie', label: t('risiken.strategie', 'Strategie'), defaultHidden: true,
      filter: { type: 'enum', options: STRATEGIES.map((s) => ({ value: s, label: s })) },
      value: (r) => r.strategie ?? '',
    },
    {
      key: 'action', label: t('risiken.action', 'Maßnahme'), defaultHidden: true,
      filter: { type: 'text' }, value: (r) => r.action ?? '',
    },
  ], [t]);

  const sf = useTableSortFilter(items, columns, { tableId: 'risiken' });

  // Dialog: vollstaendige Bearbeitung aller Felder.
  const [form, setForm] = useState<CoreFields>({
    beschreibung: '', risiko: '', umgebung: '', weight: 3, assessment: 3, strategie: '', action: '',
  });

  useEffect(() => {
    if (!editItem) return;
    setForm(coreFrom(editItem));
  }, [editItem]);

  const handleDialogSave = async () => {
    if (!editItem || !form.beschreibung.trim()) return;
    try {
      await updateMut.mutateAsync({
        id: editItem.id,
        data: {
          beschreibung: form.beschreibung.trim(),
          risiko: form.risiko.trim() || null,
          umgebung: form.umgebung.trim() || null,
          weight: form.weight, assessment: form.assessment,
          strategie: form.strategie || null, action: form.action.trim() || null,
        },
      });
      notify(tc('status.saved', 'Gespeichert'), 'success');
      setEditItem(null);
    } catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await deleteMut.mutateAsync(deleteId); notify(tc('status.deleted', 'Gelöscht'), 'success'); }
    catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
    finally { setDeleteId(null); }
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  const head = { color: 'var(--pm-fg-subtle)', fontWeight: 600 };

  const pdfMeta: ReportMeta = { projectName, pageTitle: t('risiken.title', 'Risiken') };
  const buildReport = () =>
    buildRisikenReport({
      items: items.map((r) => ({
        beschreibung: r.beschreibung, risiko: r.risiko, umgebung: r.umgebung,
        weight: r.weight, assessment: r.assessment, strategie: r.strategie,
      })),
    });

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{t('risiken.title', 'Risiken')}</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PmColumnPicker columns={sf.hideableColumns} isVisible={sf.isVisible} onToggle={sf.toggleColumn} />
          <PdfExportButton meta={pdfMeta} build={buildReport} />
        </Stack>
      </Stack>

      <PmCard flush>
        <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid var(--pm-bg-muted)' }}>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
            {t('risiken.inlineHint', 'Oben direkt eintragen und Enter drücken — Beschreibung genügt. Spalten über „Spalten" ein-/ausblenden; für alle Felder gebündelt das Stift-Symbol → große Maske.')}
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              {sf.visibleColumns.map((col) => (
                <PmHeadCell
                  key={col.key}
                  label={col.label}
                  align={col.align}
                  filter={col.filter}
                  sortActive={sf.sortKey === col.key}
                  sortDir={sf.sortDir}
                  onToggleSort={() => sf.toggleSort(col.key)}
                  filterValue={sf.filters[col.key]}
                  onSetFilter={(v) => sf.setFilter(col.key, v)}
                  onClearFilter={() => sf.clearFilter(col.key)}
                  headSx={head}
                />
              ))}
              <TableCell sx={head} align="right">{tc('actions.actions', 'Aktionen')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <AddRisikoRow isVisible={sf.isVisible} onCreate={async (core) => {
              await createMut.mutateAsync({
                beschreibung: core.beschreibung,
                risiko: core.risiko || null, umgebung: core.umgebung || null,
                weight: core.weight, assessment: core.assessment,
                strategie: core.strategie || null, action: core.action || null, status: DEFAULT_STATUS,
              });
            }} />
            {sf.rows.map((r) => (
              <RisikoRow
                key={r.id}
                item={r}
                isVisible={sf.isVisible}
                onSave={async (core) => {
                  await updateMut.mutateAsync({
                    id: r.id,
                    data: {
                      beschreibung: core.beschreibung,
                      risiko: core.risiko || null, umgebung: core.umgebung || null,
                      weight: core.weight, assessment: core.assessment,
                      strategie: core.strategie || null, action: core.action || null,
                    },
                  });
                }}
                onEditDetail={() => setEditItem(r)}
                onDelete={() => setDeleteId(r.id)}
              />
            ))}
            {sf.rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={sf.visibleColumns.length + 1}>
                  <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
                    {items.length === 0
                      ? t('risiken.emptyHint', 'Lege das erste Risiko an um zu starten.')
                      : t('risiken.filterEmpty', 'Keine Treffer fuer die aktuelle Filter-Kombination.')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </PmCard>

      {/* Risikomatrix unter der Tabelle */}
      <PmCard>
        <RisikoMatrix
          items={items.map((r) => ({ id: r.id, beschreibung: r.beschreibung, weight: r.weight, assessment: r.assessment }))}
          onMove={async (id, weight, assessment) => {
            try {
              // KRITISCH: nur weight/assessment senden — score ist GENERATED COLUMN im Backend.
              await updateMut.mutateAsync({ id, data: buildMovePatch(weight, assessment) as unknown as Record<string, unknown> });
            } catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
          }}
        />
      </PmCard>

      {/* Detail-Dialog: alle Felder */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="sm" fullWidth>
        <AppDialogTitle>{t('risiken.editTitle', 'Risiko bearbeiten')}</AppDialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField required multiline rows={2} label={t('risiken.beschreibung', 'Beschreibung')} value={form.beschreibung} onChange={(e) => setForm({ ...form, beschreibung: e.target.value })} fullWidth />
            <TextField multiline rows={2} label={t('risiken.risiko', 'Risiko')} value={form.risiko} onChange={(e) => setForm({ ...form, risiko: e.target.value })} fullWidth />
            <TextField multiline rows={2} label={t('risiken.umgebung', 'Umgebung')} value={form.umgebung} onChange={(e) => setForm({ ...form, umgebung: e.target.value })} fullWidth />
            <Box>
              <Typography variant="caption">{t('risiken.weight', 'Schwere')} ({form.weight})</Typography>
              <Slider value={form.weight} onChange={(_, v) => setForm({ ...form, weight: v as number })} min={1} max={5} step={1} marks />
            </Box>
            <Box>
              <Typography variant="caption">{t('risiken.assessment', 'Eintrittswahrscheinlichkeit')} ({form.assessment})</Typography>
              <Slider value={form.assessment} onChange={(_, v) => setForm({ ...form, assessment: v as number })} min={1} max={5} step={1} marks />
            </Box>
            <FormControl fullWidth>
              <InputLabel>{t('risiken.strategie', 'Strategie')}</InputLabel>
              <Select value={form.strategie} label={t('risiken.strategie', 'Strategie')} onChange={(e) => setForm({ ...form, strategie: e.target.value })}>
                <MenuItem value="">—</MenuItem>
                {STRATEGIES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label={t('risiken.action', 'Maßnahme')} value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} multiline rows={2} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>{tc('actions.cancel', 'Abbrechen')}</Button>
          <Button variant="contained" onClick={handleDialogSave} disabled={!form.beschreibung.trim() || updateMut.isPending}>
            {tc('actions.save', 'Speichern')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteId}
        title={t('risiken.deleteTitle', 'Risiko löschen?')}
        message={t('risiken.deleteConfirm', 'Dieses Risiko wird endgültig entfernt.')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Stack>
  );
}

function AddRisikoRow({ isVisible, onCreate }: { isVisible: (k: string) => boolean; onCreate: (core: CoreFields) => Promise<void> }) {
  const { t } = useTranslation('projects');
  const EMPTY: CoreFields = { beschreibung: '', risiko: '', umgebung: '', weight: 3, assessment: 3, strategie: '', action: '' };
  const [form, setForm] = useState<CoreFields>(EMPTY);
  const [busy, setBusy] = useState(false);
  const canCreate = form.beschreibung.trim().length > 0;

  const submit = async () => {
    if (!canCreate || busy) return;
    setBusy(true);
    try { await onCreate({ ...form, beschreibung: form.beschreibung.trim() }); setForm(EMPTY); } finally { setBusy(false); }
  };
  const onKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } };

  return (
    <TableRow sx={{ bgcolor: 'var(--pm-bg-subtle)' }}>
      <TableCell>
        <TextField variant="standard" fullWidth autoFocus placeholder={t('risiken.beschreibungPlaceholder', 'Risiko beschreiben…')} value={form.beschreibung} onChange={(e) => setForm({ ...form, beschreibung: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      {isVisible('risiko') && (
        <TableCell>
          <TextField variant="standard" fullWidth placeholder={t('risiken.risikoPlaceholder', 'Risikoereignis…')} value={form.risiko} onChange={(e) => setForm({ ...form, risiko: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      {isVisible('umgebung') && (
        <TableCell>
          <TextField variant="standard" fullWidth placeholder={t('risiken.umgebungPlaceholder', 'Kontext / Umfeld…')} value={form.umgebung} onChange={(e) => setForm({ ...form, umgebung: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      {isVisible('weight') && (
        <TableCell align="center">
          <Select variant="standard" value={form.weight} onChange={(e) => setForm({ ...form, weight: Number(e.target.value) })}>
            {LEVELS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </Select>
        </TableCell>
      )}
      {isVisible('assessment') && (
        <TableCell align="center">
          <Select variant="standard" value={form.assessment} onChange={(e) => setForm({ ...form, assessment: Number(e.target.value) })}>
            {LEVELS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </Select>
        </TableCell>
      )}
      {isVisible('strategie') && (
        <TableCell>
          <Select variant="standard" fullWidth displayEmpty value={form.strategie} onChange={(e) => setForm({ ...form, strategie: e.target.value })}>
            <MenuItem value="">—</MenuItem>
            {STRATEGIES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </TableCell>
      )}
      {isVisible('action') && (
        <TableCell>
          <TextField variant="standard" fullWidth placeholder={t('risiken.actionPlaceholder', 'Maßnahme…')} value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      <TableCell align="right">
        <IconButton color="primary" onClick={() => void submit()} disabled={!canCreate || busy} aria-label={t('risiken.add', 'Neues Risiko')}>
          <AddIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

function RisikoRow({
  item, isVisible, onSave, onEditDetail, onDelete,
}: {
  item: Risiko;
  isVisible: (k: string) => boolean;
  onSave: (core: CoreFields) => Promise<void>;
  onEditDetail: () => void;
  onDelete: () => void;
}) {
  const { t: tc } = useTranslation('common');

  const [form, setForm] = useState<CoreFields>(coreFrom(item));
  const autosave = useAutosave<CoreFields>({
    canSave: (f) => f.beschreibung.trim().length > 0,
    onSave: async (f) => { await onSave({ ...f, beschreibung: f.beschreibung.trim() }); },
  });

  useEffect(() => {
    if (autosave.isDirty) return;
    setForm(coreFrom(item));
  }, [autosave.isDirty, item.beschreibung, item.risiko, item.umgebung, item.weight, item.assessment, item.strategie, item.action]);

  const update = (patch: Partial<CoreFields>) => { const next = { ...form, ...patch }; setForm(next); autosave.schedule(next); };
  const onKeyDown = (e: React.KeyboardEvent) => handleInlineEditKeyDown(e, autosave.flush);

  return (
    <TableRow hover>
      <TableCell>
        <TextField variant="standard" fullWidth value={form.beschreibung} onChange={(e) => update({ beschreibung: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      {isVisible('risiko') && (
        <TableCell>
          <TextField variant="standard" fullWidth value={form.risiko} onChange={(e) => update({ risiko: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      {isVisible('umgebung') && (
        <TableCell>
          <TextField variant="standard" fullWidth value={form.umgebung} onChange={(e) => update({ umgebung: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      {isVisible('weight') && (
        <TableCell align="center">
          <Select variant="standard" value={form.weight} onChange={(e) => update({ weight: Number(e.target.value) })}>
            {LEVELS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </Select>
        </TableCell>
      )}
      {isVisible('assessment') && (
        <TableCell align="center">
          <Select variant="standard" value={form.assessment} onChange={(e) => update({ assessment: Number(e.target.value) })}>
            {LEVELS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </Select>
        </TableCell>
      )}
      {isVisible('strategie') && (
        <TableCell>
          <Select variant="standard" fullWidth displayEmpty value={form.strategie} onChange={(e) => update({ strategie: e.target.value })}>
            <MenuItem value="">—</MenuItem>
            {STRATEGIES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </TableCell>
      )}
      {isVisible('action') && (
        <TableCell>
          <TextField variant="standard" fullWidth value={form.action} onChange={(e) => update({ action: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
          <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} compact />
          <IconButton size="small" onClick={onEditDetail} aria-label={tc('actions.edit', 'Bearbeiten')}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={onDelete} aria-label={tc('actions.delete', 'Löschen')}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
